import { brands } from '../../data/products';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const brandOptions = brands.map(b => ({ value: b.slug, label: b.fullName, models: b.models }));

const sourceOptions = [
  'Instagram',
  'Google',
  'Friend / Referral',
  'Car Show / Event',
  'Other',
];

export default function OrderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);

  const prefilledBrand = searchParams.get('brand') || '';
  const prefilledProduct = searchParams.get('productName') || '';

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    country: '',
    brand: prefilledBrand,
    carModel: '',
    products: prefilledProduct,
    finish: 'Gloss',
    notes: '',
    source: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const selectedBrand = brandOptions.find(b => b.value === form.brand);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) newErrors.email = 'Valid email is required';
    if (!form.phone.trim()) newErrors.phone = 'Phone / WhatsApp number is required';
    if (!form.brand) newErrors.brand = 'Please select a vehicle brand';
    if (!form.products.trim()) newErrors.products = 'Please describe the parts you need';
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    const brandLabel = brandOptions.find(b => b.value === form.brand)?.label || form.brand;

    const subject = encodeURIComponent(`Order Inquiry – ${brandLabel} – ${form.fullName}`);
    const body = encodeURIComponent(
      `NEW ORDER INQUIRY\n` +
      `==================\n\n` +
      `CUSTOMER DETAILS\n` +
      `Name: ${form.fullName}\n` +
      `Email: ${form.email}\n` +
      `Phone/WhatsApp: ${form.phone}\n` +
      `Country: ${form.country || 'Not specified'}\n\n` +
      `VEHICLE DETAILS\n` +
      `Brand: ${brandLabel}\n` +
      `Model: ${form.carModel || 'Not specified'}\n\n` +
      `ORDER DETAILS\n` +
      `Parts Requested: ${form.products}\n` +
      `Finish Preference: ${form.finish}\n` +
      `Additional Notes: ${form.notes || 'None'}\n\n` +
      `How They Found Us: ${form.source || 'Not specified'}\n`
    );

    window.location.href = `mailto:info@budzevina.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bg-white min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center min-h-[80vh] px-4">
          <div className="max-w-lg w-full text-center">
            <div className="w-20 h-20 bg-black flex items-center justify-center mx-auto mb-6">
              <i className="ri-checkbox-circle-line text-white text-4xl"></i>
            </div>
            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight mb-4">Order Submitted!</h2>
            <p className="text-gray-600 text-base leading-relaxed mb-8">
              Your email client should have opened with your order details. If not, please contact us directly through any of the channels below.
            </p>
            <div className="space-y-3 mb-8 text-left">
              <a href="https://www.instagram.com/budzevina/?hl=en" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-4 border border-gray-200 px-5 py-4 hover:border-black transition-all group">
                <i className="ri-instagram-line text-black text-xl flex-shrink-0"></i>
                <div>
                  <div className="text-black font-bold text-sm">Instagram</div>
                  <div className="text-gray-400 text-xs">@budzevina</div>
                </div>
                <i className="ri-arrow-right-line text-gray-400 group-hover:text-black ml-auto transition-colors"></i>
              </a>
              <a href="https://wa.me/381642428074" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-4 border border-gray-200 px-5 py-4 hover:border-black transition-all group">
                <i className="ri-whatsapp-line text-black text-xl flex-shrink-0"></i>
                <div>
                  <div className="text-black font-bold text-sm">WhatsApp</div>
                  <div className="text-gray-400 text-xs">+381 64 242 8074</div>
                </div>
                <i className="ri-arrow-right-line text-gray-400 group-hover:text-black ml-auto transition-colors"></i>
              </a>
              <a href="tel:+381642428074"
                className="flex items-center gap-4 border border-gray-200 px-5 py-4 hover:border-black transition-all group">
                <i className="ri-phone-line text-black text-xl flex-shrink-0"></i>
                <div>
                  <div className="text-black font-bold text-sm">Phone</div>
                  <div className="text-gray-400 text-xs">+381 64 242 8074</div>
                </div>
                <i className="ri-arrow-right-line text-gray-400 group-hover:text-black ml-auto transition-colors"></i>
              </a>
            </div>
            <button onClick={() => navigate('/')}
              className="bg-black text-white px-10 py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition-all cursor-pointer inline-flex items-center gap-3">
              <i className="ri-home-line"></i>
              Back to Home
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const inputClass = (field: string) =>
    `w-full bg-white border ${errors[field] ? 'border-red-400' : 'border-gray-300'} px-4 py-3 text-black text-sm placeholder-gray-400 focus:outline-none focus:border-black transition-colors`;

  const labelClass = 'block text-black text-xs font-bold uppercase tracking-wider mb-2';

  return (
    <div className="bg-white min-h-screen">
      <Navbar />

      {/* Hero */}
      <div className="bg-black pt-32 pb-14">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider mb-4">
            <button onClick={() => navigate('/')} className="hover:text-white transition-colors cursor-pointer">Home</button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Order Inquiry</span>
          </div>
          <h1 className="text-white text-5xl md:text-7xl font-black uppercase tracking-tighter leading-none mb-3">
            Order Inquiry
          </h1>
          <p className="text-gray-400 text-base md:text-lg max-w-xl">
            Fill in your details and the parts you need. We'll confirm your order and pricing via email, WhatsApp, or phone.
          </p>
        </div>
      </div>

      {/* Trust Bar */}
      <div className="bg-zinc-900 border-b border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-4">
          <div className="flex flex-wrap items-center gap-6 md:gap-10">
            <div className="flex items-center gap-2 text-gray-300 text-xs">
              <i className="ri-shield-check-line text-white text-base"></i>
              <span>5-Year Warranty</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300 text-xs">
              <i className="ri-global-line text-white text-base"></i>
              <span>Worldwide Shipping</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300 text-xs">
              <i className="ri-temp-cold-line text-white text-base"></i>
              <span>Dry Carbon</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300 text-xs">
              <i className="ri-checkbox-circle-line text-white text-base"></i>
              <span>OEM Fitment Guaranteed</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <section className="py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-4 sm:px-8">
          <form onSubmit={handleSubmit} noValidate>
            {/* Section 1: Contact */}
            <div className="mb-10 md:mb-14">
              <div className="flex items-center gap-3 mb-6 md:mb-8">
                <div className="w-8 h-8 bg-black flex items-center justify-center text-white text-xs font-black">01</div>
                <h2 className="text-black text-xl md:text-2xl font-black uppercase tracking-tight">Your Contact Details</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>Full Name *</label>
                  <input name="fullName" value={form.fullName} onChange={handleChange}
                    type="text" placeholder="e.g. John Smith" className={inputClass('fullName')} />
                  {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
                </div>
                <div>
                  <label className={labelClass}>Email Address *</label>
                  <input name="email" value={form.email} onChange={handleChange}
                    type="email" placeholder="your@email.com" className={inputClass('email')} />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className={labelClass}>Phone / WhatsApp *</label>
                  <input name="phone" value={form.phone} onChange={handleChange}
                    type="tel" placeholder="+381 64 000 0000" className={inputClass('phone')} />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <label className={labelClass}>Country</label>
                  <input name="country" value={form.country} onChange={handleChange}
                    type="text" placeholder="e.g. Serbia, Germany, UAE…" className={inputClass('country')} />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 mb-10 md:mb-14"></div>

            {/* Section 2: Vehicle */}
            <div className="mb-10 md:mb-14">
              <div className="flex items-center gap-3 mb-6 md:mb-8">
                <div className="w-8 h-8 bg-black flex items-center justify-center text-white text-xs font-black">02</div>
                <h2 className="text-black text-xl md:text-2xl font-black uppercase tracking-tight">Vehicle Details</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>Vehicle Brand *</label>
                  <select name="brand" value={form.brand} onChange={handleChange} className={inputClass('brand')}>
                    <option value="">Select brand…</option>
                    {brandOptions.map(b => (
                      <option key={b.value} value={b.value}>{b.label}</option>
                    ))}
                    <option value="other">Other / Custom</option>
                  </select>
                  {errors.brand && <p className="text-red-500 text-xs mt-1">{errors.brand}</p>}
                </div>
                <div>
                  <label className={labelClass}>Specific Model</label>
                  {selectedBrand ? (
                    <select name="carModel" value={form.carModel} onChange={handleChange} className={inputClass('carModel')}>
                      <option value="">Select model…</option>
                      {selectedBrand.models.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                      <option value="Other">Other / Not Listed</option>
                    </select>
                  ) : (
                    <input name="carModel" value={form.carModel} onChange={handleChange}
                      type="text" placeholder="e.g. GT3 RS, 695 Biposto…" className={inputClass('carModel')} />
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 mb-10 md:mb-14"></div>

            {/* Section 3: Order */}
            <div className="mb-10 md:mb-14">
              <div className="flex items-center gap-3 mb-6 md:mb-8">
                <div className="w-8 h-8 bg-black flex items-center justify-center text-white text-xs font-black">03</div>
                <h2 className="text-black text-xl md:text-2xl font-black uppercase tracking-tight">Order Details</h2>
              </div>
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>Parts / Products Requested *</label>
                  <textarea name="products" value={form.products} onChange={handleChange}
                    rows={4}
                    placeholder="e.g. Front splitter, side skirts, rear diffuser, mirror caps…"
                    className={`${inputClass('products')} resize-none`} />
                  {errors.products && <p className="text-red-500 text-xs mt-1">{errors.products}</p>}
                </div>

                <div>
                  <label className={labelClass}>Finish Preference</label>
                  <div className="grid grid-cols-3 gap-3">
                    {['Gloss', 'Matte', 'Forged'].map(finish => (
                      <button
                        key={finish}
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, finish }))}
                        className={`py-3 text-sm font-bold uppercase tracking-wider border-2 transition-all cursor-pointer ${
                          form.finish === finish
                            ? 'bg-black text-white border-black'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-black'
                        }`}
                      >
                        {finish}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Additional Notes / Custom Requirements</label>
                  <textarea name="notes" value={form.notes} onChange={handleChange}
                    rows={3}
                    placeholder="Any special requirements, colour preferences, deadlines, or questions…"
                    className="w-full bg-white border border-gray-300 px-4 py-3 text-black text-sm placeholder-gray-400 focus:outline-none focus:border-black transition-colors resize-none" />
                </div>

                <div>
                  <label className={labelClass}>How Did You Find Us?</label>
                  <select name="source" value={form.source} onChange={handleChange}
                    className="w-full bg-white border border-gray-300 px-4 py-3 text-black text-sm focus:outline-none focus:border-black transition-colors">
                    <option value="">Select…</option>
                    {sourceOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Notice */}
            <div className="bg-gray-50 border border-gray-200 p-5 mb-8 flex gap-4">
              <i className="ri-information-line text-black text-xl flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-sm leading-relaxed">
                Submitting this form will open your email client with your order details pre-filled. We'll reply within 24 hours to confirm pricing, availability, and shipping options.
                Alternatively, contact us directly via WhatsApp at <a href="https://wa.me/381642428074" className="text-black font-bold underline">+381 64 242 8074</a>.
              </p>
            </div>

            <button
              type="submit"
              className="w-full bg-black text-white py-5 font-bold text-base uppercase tracking-wider hover:bg-gray-800 transition-all flex items-center justify-center gap-3 cursor-pointer"
            >
              <i className="ri-send-plane-line text-xl"></i>
              Send Order Inquiry
            </button>
          </form>
        </div>
      </section>

      {/* Contact Strip */}
      <div className="bg-black py-12">
        <div className="max-w-[900px] mx-auto px-4 sm:px-8">
          <h3 className="text-white text-xl font-black uppercase tracking-tight text-center mb-6">Or Reach Us Directly</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <a href="https://www.instagram.com/budzevina/?hl=en" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 px-5 py-4 hover:border-white transition-all group">
              <i className="ri-instagram-line text-white text-xl flex-shrink-0"></i>
              <div>
                <div className="text-white text-sm font-semibold">Instagram</div>
                <div className="text-gray-400 text-xs">@budzevina</div>
              </div>
              <i className="ri-arrow-right-line text-gray-600 group-hover:text-white ml-auto transition-colors"></i>
            </a>
            <a href="https://wa.me/381642428074" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 px-5 py-4 hover:border-white transition-all group">
              <i className="ri-whatsapp-line text-white text-xl flex-shrink-0"></i>
              <div>
                <div className="text-white text-sm font-semibold">WhatsApp</div>
                <div className="text-gray-400 text-xs">+381 64 242 8074</div>
              </div>
              <i className="ri-arrow-right-line text-gray-600 group-hover:text-white ml-auto transition-colors"></i>
            </a>
            <a href="tel:+381642428074"
              className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 px-5 py-4 hover:border-white transition-all group">
              <i className="ri-phone-line text-white text-xl flex-shrink-0"></i>
              <div>
                <div className="text-white text-sm font-semibold">Phone</div>
                <div className="text-gray-400 text-xs">+381 64 242 8074</div>
              </div>
              <i className="ri-arrow-right-line text-gray-600 group-hover:text-white ml-auto transition-colors"></i>
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
